import React, { forwardRef, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import arrayMutators from 'final-form-arrays';
import { Form } from 'react-final-form';
import { Button } from 'ncoded-component-library';
import ArrowupIcon from 'icons/Arrowup.icon';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import TableControlButton from 'components/TableControlButton';
import TablePanelDropdown from 'components/TablePanelDropdown';
import useCallbackRef from 'hooks/useCallbackRef';
import MultiSortOptions, {
  MultiSortOptionsProps,
} from 'components/MultiSortModal/components/MultiSortOptions/MultiSortOptions.component';
import useMultiSortForm from 'components/MultiSortModal/hooks/useMultiSortForm';
import { TablePanelDropdownRef } from 'components/TablePanelDropdown/TablePanelDropdown.component';
import { getHasElementsInArray } from 'utils';
import { FocusFieldRef, OpenFieldPanelRef } from 'models/FieldFocusableModal';
import useOpenFieldPanel from 'hooks/useOpenFieldPanel';

import './MultiSortPanel.styles.scss';

type MultiSortPanelProps = {
  className?: string;
  sortParam?: string;
} & MultiSortOptionsProps;

const MultiSortPanel: React.ForwardRefRenderFunction<
  OpenFieldPanelRef,
  MultiSortPanelProps
> = (props, ref) => {
  const {
    className,
    options = [],
    sortDirectionTranslations,
    shouldSortOptions,
    sortParam,
  } = props;

  const classes = classNames(
    'multi-sort-panel',
    'table-panel-dropdown--form-panel',
    className,
  );

  const { t } = useTranslation();

  const [panel, panelRef] = useCallbackRef<TablePanelDropdownRef>();

  const multiSortOptionsRef = useRef<FocusFieldRef>(null);

  useOpenFieldPanel(ref, panel, multiSortOptionsRef);

  const { initialValues, onSubmit } = useMultiSortForm({
    sortParam,
    onDone: panel?.close,
  });

  if (!getHasElementsInArray(options)) return null;

  return (
    <Form
      mutators={{ ...arrayMutators }}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {({ invalid, handleSubmit }) => (
        <TablePanelDropdown
          ref={panelRef}
          className={classes}
          title={t('sort')}
          trigger={
            <div className="table-panel-dropdown__trigger-wrapper">
              <TableControlButton
                tooltip={t('sort')}
                icon={
                  <>
                    <ArrowupIcon />
                    <ArrowdownIcon />
                  </>
                }
              />
            </div>
          }
          footer={
            <>
              <Button variant="link" type="button" onClick={panel?.close}>
                {t('cancel')}
              </Button>
              <Button disabled={invalid} onClick={handleSubmit}>
                {t('sort')}
              </Button>
            </>
          }
        >
          <form className="multi-sort-panel__body" onSubmit={handleSubmit}>
            <MultiSortOptions
              ref={multiSortOptionsRef}
              options={options}
              sortDirectionTranslations={sortDirectionTranslations}
              shouldSortOptions={shouldSortOptions}
            />
          </form>
        </TablePanelDropdown>
      )}
    </Form>
  );
};

export default forwardRef(MultiSortPanel);
